// The Gmail / Outlook connect round-trip (plan §1.7, §4.2).
//
// Ported from the two upstream call sites that build these URLs by hand —
// `external-email-signin.page.component.ng1.ts` and `extension-alert-candidates-email-signin.ts`
// — which is why it lives here rather than being written twice again.
//
// ⚠️ The `origin` parameter is the part that does not exist upstream, and it is not optional
// for this port. careero redirects server-side to `<origin>chrome-extension/post?type=…`,
// where the post page hands the result to the extension over a BroadcastChannel — and
// BroadcastChannel is origin-scoped, so a redirect to the tenant is a flow that silently never
// completes. `ExtensionOAuthRedirectResolver` resolves the return origin from, in order: this
// parameter, the request's `Origin`, its `Referer`. The last two are both rewritten to the
// tenant by our own proxy (`apps/server/src/cookies.ts`), so only this parameter survives.
//
// The tenant must also list the origin in `extension.oauth.post.allowed.origins`, or the
// resolver drops it and falls back to WEBSITE_MAIN_URL — behaving exactly as it does today.

/** Verbatim from `ExtensionEmailIntegrationPhase` (both the TS enum and the Java one). */
export type EmailIntegrationPhase = 'EMAIL_SIGNUP' | 'SHORTLIST'

export type EmailIntegrationProvider = 'google' | 'outlook'

export interface ConnectUrlOptions {
  /** Upstream omits `phase` entirely when the flow carries an `alertId`. */
  phase?: EmailIntegrationPhase
  /** Defaults to this document's origin, which is what the redirect has to come back to. */
  origin?: string
}

/**
 * `/integrations/{google,outlook}/connect` — note the prefix: it is NOT under `/oauth`, which
 * on the tenant belongs to Salesforce and which the extension never calls. Both paths are in
 * `PROXY_PREFIXES`, so this URL is same-origin and the proxy forwards it.
 */
export function emailIntegrationConnectUrl(
  provider: EmailIntegrationProvider,
  options: ConnectUrlOptions = {},
): string {
  const { phase, origin = window.location.origin } = options
  const params = new URLSearchParams()
  if (phase !== undefined) params.set('phase', phase)
  params.set('origin', origin)
  return `/integrations/${provider}/connect?${params.toString()}`
}
